import { useEffect, useState, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface SessionStep {
  id: string;
  question_text: string;
  user_answer: string | null;
  answer_summary: string | null;
  step_number: number;
  phase: number;
  subphase: string;
  clarity_score: number | null;
  created_at: string;
  updated_at: string;
  created_by: string;
  creator_name: string | null;
  creator_role: string | null;
  question_id: string;
  /** True when this step arrived via realtime and hasn't been "seen" yet */
  isNew?: boolean;
}

export const useSessionSteps = (sessionId: string | undefined) => {
  const [steps, setSteps] = useState<SessionStep[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const knownIdsRef = useRef<Set<string>>(new Set());
  // Track the latest step event for toast notifications
  const [latestLiveStep, setLatestLiveStep] = useState<{ step: SessionStep; eventType: 'INSERT' | 'UPDATE' } | null>(null);

  const loadSteps = useCallback(async (markNewIds?: Set<string>) => {
    if (!sessionId) return;

    const { data, error } = await supabase
      .from("decision_steps")
      .select(`
        id, question_text, user_answer, answer_summary, step_number,
        phase, subphase, clarity_score, created_at, updated_at, created_by,
        question_id,
        profiles!decision_steps_created_by_fkey(name)
      `)
      .eq("session_id", sessionId)
      .order("step_number", { ascending: true });

    if (error) {
      console.error("Error fetching session steps:", error);
      return;
    }

    const { data: participants } = await supabase
      .from("decision_participants")
      .select("user_id, role")
      .eq("session_id", sessionId);

    const roleMap = new Map((participants || []).map(p => [p.user_id, p.role]));

    const mapped: SessionStep[] = (data || []).map((s: any) => ({
      id: s.id,
      question_text: s.question_text,
      user_answer: s.user_answer,
      answer_summary: s.answer_summary,
      step_number: s.step_number,
      phase: s.phase,
      subphase: s.subphase,
      clarity_score: s.clarity_score,
      created_at: s.created_at,
      updated_at: s.updated_at,
      created_by: s.created_by,
      creator_name: s.profiles?.name || null,
      creator_role: roleMap.get(s.created_by) || null,
      question_id: s.question_id,
      isNew: markNewIds ? markNewIds.has(s.id) : false,
    }));

    // Update known IDs
    knownIdsRef.current = new Set(mapped.map(s => s.id));
    setSteps(mapped);
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) {
      setIsLoading(false);
      return;
    }

    const init = async () => {
      setIsLoading(true);
      await loadSteps();
      setIsLoading(false);
    };
    init();

    const channel = supabase
      .channel(`session_steps_${sessionId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "decision_steps",
          filter: `session_id=eq.${sessionId}`,
        },
        (payload) => {
          const newId = (payload.new as any)?.id;
          const newIds = newId ? new Set([newId]) : undefined;
          loadSteps(newIds).then(() => {
            // Emit live step event for toast
            if (newId) {
              // We'll resolve the full step after reload
              setSteps(prev => {
                const found = prev.find(s => s.id === newId);
                if (found) {
                  setLatestLiveStep({ step: found, eventType: 'INSERT' });
                }
                return prev;
              });
            }
          });
        }
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "decision_steps",
          filter: `session_id=eq.${sessionId}`,
        },
        (payload) => {
          const updatedId = (payload.new as any)?.id;
          const updatedIds = updatedId ? new Set([updatedId]) : undefined;
          loadSteps(updatedIds).then(() => {
            if (updatedId) {
              setSteps(prev => {
                const found = prev.find(s => s.id === updatedId);
                if (found) {
                  setLatestLiveStep({ step: found, eventType: 'UPDATE' });
                }
                return prev;
              });
            }
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [sessionId, loadSteps]);

  /** Mark a step as "seen" (removes the isNew badge) */
  const markStepSeen = useCallback((stepId: string) => {
    setSteps(prev => prev.map(s => s.id === stepId ? { ...s, isNew: false } : s));
  }, []);

  /** Clear the latest live step after consuming it */
  const clearLatestLiveStep = useCallback(() => {
    setLatestLiveStep(null);
  }, []);

  return { steps, isLoading, markStepSeen, latestLiveStep, clearLatestLiveStep };
};
